<!-- =========================================================================================
  File Name: UserView.vue
  Description: User View page

========================================================================================== -->

<template>
  <div id="page-user-view">

    <vs-alert :active.sync="userNotFound" color="danger" title="User Not Found">
      <span>User record with id: {{ $route.params.userId }} not found. </span>
      <span>
        <span>Check </span>
        <router-link to="/" class="text-inherit underline">All Users</router-link>
      </span>
    </vs-alert>

    <div v-if="userData">

      <!--- TOP USER BANNER START --->
      <div class="vx-row mb-6">
        <div class="vx-col w-full">
          <div class="rounded-lg w-full"
               style="background-image: url(https://cdn.discordapp.com/attachments/733851758211629347/954057924391600218/BG.png);
         box-shadow: 0px 4px 25px 0px rgba(0,0,0,.1);">
            <div class="knowledge-base-jumbotron-content rounded-lg lg:p-12 sm:p-10 p-8"
                 style="backdrop-filter: blur(10px)">
              <div class="flex justify-between items-center ">
                <div>
                  <h5 class="font-light text-white ml-2">{{ userData.role.replaceAll('_', ' ') | capitalize }}</h5>
                  <h1 class="text-white font-bold" style="font-size: 4rem">{{ userData.full_name }}</h1>
                </div>
                <img v-if="userData.profile_picture" :src="userData.profile_picture" class="rounded-lg" width="175">
              </div>
            </div>
          </div>
        </div>
      </div>
      <!--- TOP USER BANNER END --->

      <!--- USER ACTION BAR START --->
      <div class="vx-row mb-6 match-height">
        <div class="vx-col grid grid-cols-3 gap-8 w-full">
          <vx-card class="p-6 cursor-pointer text-white" @click="editUserInit"
                   :style="`background-image: linear-gradient(118deg, rgba(var(--vs-primary), 0.4), rgba(var(--vs-primary), .2)), url(${cardBgImgURL})`">
            <div class="flex items-center justify-center">
              <feather-icon icon="EditIcon" svgClasses="h-16 w-16"/>
            </div>
            <div class="flex items-center justify-center">
              <h2 class="text-center mt-4 text-white">Edit<br>User</h2>
            </div>
          </vx-card>
          <vx-card class="p-6 cursor-pointer text-white" @click="courseSearchActive = true"
                   :style="`background-image: linear-gradient(118deg, rgba(var(--vs-success), 0.4), rgba(var(--vs-success), .2)), url(${cardBgImgURL})`">
            <div class="flex items-center justify-center">
              <feather-icon icon="BookIcon" svgClasses="h-16 w-16"/>
            </div>
            <div class="flex items-center justify-center">
              <h2 class="text-center mt-4 text-white">Assign<br>Course</h2>
            </div>
          </vx-card>
          <vx-card class="p-6 cursor-pointer text-white"
                   @click="groupManagerActive = true"
                   :style="`background-image: linear-gradient(118deg, rgba(var(--vs-warning), 0.4), rgba(var(--vs-warning), .2)), url(${cardBgImgURL})`">
            <div class="flex items-center justify-center">
              <feather-icon icon="TagIcon" svgClasses="h-16 w-16"/>
            </div>
            <div class="flex items-center justify-center">
              <h2 class="text-center mt-4 text-white">Groups<br>Manager</h2>
            </div>
          </vx-card>
        </div>
      </div>
      <!--- USER ACTION BAR END --->

      <!--- USER SHOW INFORMATION START--->
      <div class="vx-row mb-6 match-height">
        <div class="vx-col w-1/3">
          <vx-card class="w-full flex items-center">
            <h3 class="font-bold">User Id</h3>
            <h3 class="font-light">{{ userData.id }}</h3>
          </vx-card>
        </div>
        <div class="vx-col w-1/3">
          <vx-card class="w-full flex items-center">
            <h3 class="font-bold">User Email</h3>
            <h3 class=" font-light">{{ userData.email }}</h3>
          </vx-card>
        </div>
        <div class="vx-col w-1/3 ">
          <vx-card class="p-1">
            <div class="grid grid-cols-1 gap-4 ">
              <vs-button class="w-full" color="danger" icon="icon-user-x" icon-pack="feather"
                         v-if="authData.active" @click="toggleUserSuspend">
                Suspend
              </vs-button>
              <vs-button class="w-full" color="success" icon="icon-user-check" icon-pack="feather"
                         v-if="!authData.active" @click="toggleUserSuspend">
                Unsuspend
              </vs-button>
            </div>
          </vx-card>
        </div>
      </div>
      <!--- USER SHOW INFORMATION END--->

      <!--- USER SHOW SUBSCRIPTIONS START--->
      <div class="vx-row ">
        <div class="vx-col w-full">
          <list-subscriptions title="User Subscriptions" :key="subTableKey" :userId="userData.id" :filterUsers="false"/>
        </div>
      </div>
      <!--- USER SHOW SUBSCRIPTIONS START--->

      <vs-popup :active.sync="userEditActive" title="Create New User">
        <div class="popup-input">
          <p class="mb-1 font-semibold">First Name</p>
          <vs-input v-model="userEditData.first_name" class="mb-3 w-full"/>

          <p class="mb-1 font-semibold">Last Name</p>
          <vs-input v-model="userEditData.last_name" class="mb-3 w-full"/>

          <p class="mb-1 font-semibold">Email</p>
          <vs-input v-model="userEditData.email" class="mb-3 w-full"/>

          <p class="mb-1 font-semibold">New Password</p>
          <vs-input v-model="userEditData.password" class="mb-3 w-full"/>

          <vs-button color="success" type="filled" @click="editUserSave">Save Changes</vs-button>
        </div>
      </vs-popup>
      <vs-popup :active.sync="courseSearchActive" title="Assign Course To User - Select Course">
        <vs-table :data="courseSearchResults" :sst="true" max-items="5" search
                  @search="addStudentCourseSearch">
          <template slot="header">
            <h3 class="ml-4">Courses</h3>
          </template>

          <template slot="thead">
            <vs-th>Id</vs-th>
            <vs-th>Name</vs-th>
            <vs-th>Action</vs-th>
          </template>

          <template slot-scope="{data}">
            <vs-tr v-for="(attach) in data" :key="attach.id">

              <vs-td :data="attach.id">
                {{ attach.id }}
              </vs-td>

              <vs-td :data="`${attach.name}`">
                <div class="flex align-middle items-center">
                  <img :src="attach.thumbnail" class="rounded mr-3" width="40px">
                  {{ `${attach.name}` }}
                </div>
              </vs-td>

              <vs-td>
                <vs-button color="primary" size="small" type="border" @click="addStudentToCourseApps(attach)">
                  Assign
                </vs-button>
              </vs-td>
            </vs-tr>
          </template>
        </vs-table>
      </vs-popup>
      <vs-popup :active.sync="appSearchActive" title="Assign Course To User - Select Apps">
        <vs-table :data="appSearchResults" :sst="true" max-items="5" search @search="addStudentAppsSearch" class="mb-2">
          <template slot="header">
            <h3 class="ml-4">Search Apps</h3>
          </template>

          <template slot="thead">
            <vs-th>Id</vs-th>
            <vs-th>Name</vs-th>
            <vs-th>Action</vs-th>
          </template>

          <template slot-scope="{data}">
            <vs-tr v-for="(attach) in data" :key="attach.id">

              <vs-td :data="attach.id">
                {{ attach.id }}
              </vs-td>

              <vs-td :data="`${attach.name}`">
                <div class="flex align-middle items-center">
                  <img :src="attach.icon" class="rounded mr-3" width="40px">
                  {{ `${attach.name}` }}
                </div>
              </vs-td>

              <vs-td>
                <vs-button color="primary" size="small" type="border" @click="addStudentToCourseAssignApp(attach)">
                  Assign
                </vs-button>
              </vs-td>
            </vs-tr>
          </template>
        </vs-table>
        <vs-divider/>
        <vs-table :data="appsSelected" max-items="5" pagination class="mt-2">
          <template slot="header">
            <h3 class="ml-4 mt-4 mb-3">Assigned Apps</h3>
          </template>

          <template slot="thead">
            <vs-th>Id</vs-th>
            <vs-th>Name</vs-th>
            <vs-th>Action</vs-th>
          </template>

          <template slot-scope="{data}">
            <vs-tr v-for="(attach, index) in data" :key="attach.id">

              <vs-td :data="attach.id">
                {{ attach.id }}
              </vs-td>

              <vs-td :data="`${attach.name}`">
                <div class="flex align-middle items-center">
                  <img :src="attach.icon" class="rounded mr-3" width="40px">
                  {{ `${attach.name}` }}
                </div>
              </vs-td>

              <vs-td>
                <vs-button color="primary" size="small" type="border"
                           @click="appsSelected.splice(index, 1)">
                  Remove
                </vs-button>
              </vs-td>

            </vs-tr>
          </template>
        </vs-table>
        <vs-button color="primary" type="filled" class="mt-3" @click="addStudentToCourseCalendar">Next Step</vs-button>
      </vs-popup>
      <vs-popup :active.sync="calendarActive" title="Assign Course To User - Select Dates">
        <div class="mb-4">
          <h6>Choose Subscription Expiry</h6>
          <datepicker :inline="true" placeholder="Select Date" v-model="calendarSelect"
                      class="datepicker-popup-inline mt-4"></datepicker>
          <ul class="flex mt-5" :key="courseAssignKey">
            <li>
              <vs-radio v-model="calendarDuration" @input="calculateCalendarDate" vs-value="1" class="mr-4">1 Month
              </vs-radio>
            </li>
            <li>
              <vs-radio v-model="calendarDuration" @input="calculateCalendarDate" vs-value="3" class="mr-4">3 Months
              </vs-radio>
            </li>
            <li>
              <vs-radio v-model="calendarDuration" @input="calculateCalendarDate" vs-value="6" class="mr-4">6 Months
              </vs-radio>
            </li>
            <li>
              <vs-radio v-model="calendarDuration" @input="calculateCalendarDate" vs-value="12" class="mr-4">1 Year
              </vs-radio>
            </li>
          </ul>
        </div>
        <p class="text-danger font-bold " v-if="calendarSelect === null">You Must Choose A
          Subscription
          Duration</p>

        <vs-button color="primary" type="filled"
                   @click="addStudentToCourseEnroll(courseAssignSelected)"
                   v-if="calendarSelect">Assign Student
        </vs-button>

      </vs-popup>
      <vs-popup :active.sync="groupManagerActive" title="Group Manager">
        <vs-table :data="groupManagerResults" :sst="true" max-items="3" search
                  @search="addStudentGroupSearch">
          <template slot="header">
            <h3 class="ml-4">Search Groups</h3>
          </template>

          <template slot="thead">
            <vs-th>Id</vs-th>
            <vs-th>Name</vs-th>
            <vs-th>Action</vs-th>
          </template>

          <template slot-scope="{data}">
            <vs-tr v-for="(attach) in data" :key="attach.id">

              <vs-td :data="attach.id">
                {{ attach.id }}
              </vs-td>

              <vs-td :data="`${attach.name}`">
                <div class="flex align-middle items-center">
                  <vs-avatar :color="attach.color" :text="attach.name.charAt(0).toUpperCase()"
                             class="flex-shrink-0 mr-2" size="30px"/>
                  {{ `${attach.name}` }}
                </div>
              </vs-td>

              <vs-td>
                <vs-button color="primary" size="small" type="border" @click="addStudentToGroup(attach)">
                  Assign
                </vs-button>
              </vs-td>
            </vs-tr>
          </template>
        </vs-table>

        <vs-divider/>
        <vs-table :data="groupData" max-items="3" search @search="addStudentCourseSearch" :key="groupManagerKey"
                  pagination>
          <template slot="header">
            <h3 class="ml-4">User Groups</h3>
          </template>

          <template slot="thead">
            <vs-th>Id</vs-th>
            <vs-th>Name</vs-th>
            <vs-th>Action</vs-th>
          </template>

          <template slot-scope="{data}">
            <vs-tr v-for="(attach) in data" :key="attach.id">

              <vs-td :data="attach.id">
                {{ attach.id }}
              </vs-td>

              <vs-td :data="`${attach.name}`">
                <div class="flex align-middle items-center">
                  <vs-avatar :color="attach.color" :text="attach.name.charAt(0).toUpperCase()" class="flex-shrink-0 mr-2"
                             size="30px"/>
                  {{ `${attach.name}` }}
                </div>
              </vs-td>

              <vs-td>
                <vs-button color="primary" size="small" type="border" @click="removeStudentFromGroup(attach)">
                  Remove
                </vs-button>
              </vs-td>
            </vs-tr>
          </template>
        </vs-table>
      </vs-popup>
    </div>
    <div class="simple-spinner" v-else>
      <span></span>
    </div>

  </div>
</template>

<script>
import ListSubscriptions from '../../../subscriptions/admin/subscription-list/ListSubscriptions'
import Datepicker from 'vuejs-datepicker'

export default {
  components: {
    ListSubscriptions,
    Datepicker
  },
  data () {
    return {
      cardBgImgURL: 'https://media.discordapp.net/attachments/733851758211629347/954057924630700132/BG2.png?width=1352&height=676',

      userData: null,
      groupData: [],
      authData: null,

      userNotFound: false,
      activeCourseData: null,

      userEditActive: false,
      userEditData: {
        id: null,
        email: null,
        first_name: null,
        last_name: null,
        password: null,
        active: false
      },

      courseSearchActive: false,
      courseSearchResults: [],

      appSearchActive: false,
      appSearchResults: [],
      appsSelected: [],

      calendarActive: false,
      calendarDuration: null,
      calendarSelect: null,

      courseAssignKey: 0,
      courseAssignSelected: {},

      subTableKey: 0,

      groupManagerActive: false,
      groupManagerResults: [],
      groupManagerKey: 0


    }
  },
  methods: {
    addStudentCourseSearch (term) {
      this.$http.get(`courses?search=${term}&amount=3`)
        .then(response => {
          if (response.data) {
            this.courseSearchResults = response.data.data
          }
        })
    },
    addStudentAppsSearch (term) {
      this.$http.get(`platform/apps?search=${term}&amount=5`)
        .then(response => {
          if (response.data) {
            this.appSearchResults = response.data.data
          }
        })
    },
    addStudentToCourseApps (course) {
      this.courseSearchActive = false
      this.appSearchActive = true
      this.courseAssignSelected = course
    },
    addStudentToCourseCalendar () {
      this.appSearchActive = false
      this.calendarActive = true
    },
    addStudentToCourseAssignApp (app) {
      let addApp = true
      this.appsSelected.forEach(item => {
        if (item.id === app.id) {
          addApp = false
          return false
        }
      })

      if (addApp === true) {
        this.appsSelected.push(app)
      } else {
        this.$vs.notify({
          title: 'App Already Assigned',
          text: 'The app you tried to assign has already been assigned',
          color: 'danger',
          iconPack: 'feather',
          position: 'bottom-right',
          icon: 'icon-alert-circle'
        })
      }
    },
    addStudentToCourseEnroll (course) {

      this.$vs.loading()

      if (!this.calendarSelect) {
        this.$vs.notify({
          color: 'danger',
          title: 'Failed to create Subscription',
          text: 'Please provide subscription duration.',
          icon: 'feather',
          iconPack: 'icon-x-circle',
          position: 'top-right'
        })
        return this.$vs.loading.close()
      }

      const apps = []
      this.appsSelected.forEach(app => {
        apps.push(app.id)
      })

      const pl = {
        assigner: JSON.parse(localStorage.getItem('userInfo')).id,
        user_id: this.userData.id,
        course_id: course.id,
        status: 'active',
        apps
      }

      pl.ending_date = this.calendarSelect.getTime()

      this.$http.post('subscriptions/create', pl)
        .then(response => {
          this.$vs.loading.close()

          if (response.status === 200) {
            return this.$vs.notify({
              color: 'success',
              title: 'Subscription Created',
              text: 'You have successfully created a subscription',
              icon: 'feather',
              iconPack: 'icon-check-circle',
              position: 'top-right'
            })
          }

        }).catch(exception => {
          this.$vs.loading.close()
          return this.$vs.notify({
            color: 'danger',
            title: 'Failed to create Subscription',
            text: exception.response.data.error.description,
            icon: 'feather',
            iconPack: 'icon-x-circle',
            position: 'top-right'
          })
        })

      this.subTableKey++
      this.calendarActive = false
      this.courseSearchActive = true

    },
    addStudentGroupSearch (term) {
      this.groupManagerResults = []
      this.$http.get(`groups?search=${term}&amount=5`)
        .then(response => {
          if (response.data) {
            this.groupManagerResults = response.data.data
          }
        })
    },
    addStudentToGroup (group) {
      this.$http.post(`users/${this.userData.id}/groups?id=${group.id}`)
        .then(response => {
          if (response.data) {
            this.loadGroupData(response.data.data.groups)
            return this.$vs.notify({
              title: 'Added User To Group',
              text: `Added ${this.userData.first_name} to ${group.name}`,
              color: 'success',
              iconPack: 'feather',
              position: 'bottom-right',
              icon: 'icon-check-circle'
            })
          }
        }).catch(exception => {
          this.$vs.loading.close()
          return this.$vs.notify({
            color: 'danger',
            title: 'Failed to Add User',
            text: exception.response.data.error.description,
            icon: 'feather',
            iconPack: 'icon-x-circle',
            position: 'top-right'
          })
        })
    },
    calculateCalendarDate () {
      const date = new Date()
      const months = parseInt(this.calendarDuration)
      date.setMonth(date.getMonth() + months)
      this.calendarSelect = date
    },
    toggleUserSuspend () {
      if (this.activeUserInfo.id === this.userData.id) {
        return this.$vs.notify({
          title: 'User Account Not Suspended',
          text: 'You cannot suspend your own user accounts',
          color: 'success',
          iconPack: 'feather',
          position: 'bottom-right',
          icon: 'icon-x-circle'
        })
      }
      const pl = {active: !this.authData.active}
      this.$http.post(`users/${this.userData.id}`, pl)
        .then(response => {
          if (response.data) {
            this.loadUserData()
            return this.$vs.notify({
              title: 'User Account Status Updated',
              text: pl.active ? 'User accounts status set to active' : 'User accounts status set to suspended',
              color: 'success',
              iconPack: 'feather',
              position: 'bottom-right',
              icon: 'icon-check-circle'
            })
          }
        }).catch(exception => {
          this.$vs.loading.close()
          return this.$vs.notify({
            color: 'danger',
            title: 'Failed to Update User Status',
            text: exception.response.data.error.description,
            icon: 'feather',
            iconPack: 'icon-x-circle',
            position: 'top-right'
          })
        })
    },
    editUserInit () {
      this.userEditActive = true
      this.userEditData = this.userData
      this.userEditData.active = this.authData.active
    },
    editUserSave () {
      this.$http.post(`users/${this.userData.id}`, this.userEditData)
        .then(response => {
          if (response.data) {
            this.loadUserData()
            if (this.userEditData.password) {
              this.$vs.notify({
                title: 'User Password Updated',
                text: `Updated users password with name ${this.userEditData.first_name} ${this.userEditData.last_name} successfully`,
                color: 'warning',
                iconPack: 'feather',
                position: 'bottom-right',
                icon: 'icon-info'
              })
            }
            return this.$vs.notify({
              title: 'Updated User',
              text: `Updated user with name ${this.userEditData.first_name} ${this.userEditData.last_name} successfully`,
              color: 'success',
              iconPack: 'feather',
              position: 'bottom-right',
              icon: 'icon-check-circle'
            })
          }
        }).catch(exception => {
          this.$vs.loading.close()
          return this.$vs.notify({
            color: 'danger',
            title: 'Failed to Update User',
            text: exception.response.data.error.description,
            icon: 'feather',
            iconPack: 'icon-x-circle',
            position: 'top-right'
          })
        })
    },
    loadGroupData (groups) {
      const newGroups = []
      groups.forEach(item => {
        this.$http.get(`groups/${item}`)
          .then(response => {
            if (response.data) {
              newGroups.push(response.data.data)
            }
          })
      })
      this.groupData = newGroups
      this.groupManagerKey++

    },
    loadUserData () {
      const userId = this.$route.params.userId

      this.$http.get(`users/${userId}?admin=true`)
        .then(response => {
          if (response.data.data) {
            this.userData = response.data.data.user
            this.authData = response.data.data.auth
            const pictureId = this.userData.profile_picture || 'default'
            this.userData.photoURL = `https://portal-cdn.rapid.education/profile-pictures/${pictureId}.jpeg`
            // this.loadUserProgress()
            this.loadGroupData(this.userData.groups)
          }
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.userNotFound = true
          }
        })
    },
    loadUserProgress () {

      if (this.userData.active_course) {

        /* this.$http.get(`progress/user/specific/@me/course/progress?id=${this.userData.active_course.id}`)
          .then(response => {
            this.coursePercent = response.data.data.completion_percent
          }) */

        this.$http.get(`courses/${this.userData.active_course}`)
          .then(response => {
            if (response.status === 200) {
              response.data.data.selected = true
              this.activeCourseData = response.data.data
            }
          }).catch(() => {
          })

      } else {
        this.activeCourseData = {
          selected: false
        }
      }

    },
    removeStudentFromGroup (group) {
      this.$http.delete(`users/${this.userData.id}/groups?id=${group.id}`)
        .then(response => {
          if (response.data) {
            this.loadGroupData(response.data.data.groups)
            return this.$vs.notify({
              title: 'Removed User To Group',
              text: `Removed ${this.userData.first_name} to ${group.name}`,
              color: 'success',
              iconPack: 'feather',
              position: 'bottom-right',
              icon: 'icon-check-circle'
            })
          }
        }).catch(exception => {
          this.$vs.loading.close()
          return this.$vs.notify({
            color: 'danger',
            title: 'Failed to Remove User',
            text: exception.response.data.error.description,
            icon: 'feather',
            iconPack: 'icon-x-circle',
            position: 'top-right'
          })
        })
    }

  },
  created () {
    this.addStudentCourseSearch('')
    this.addStudentGroupSearch('')
    this.addStudentAppsSearch('')
    this.loadUserData()
  },
  beforeDestroy () {
    this.userData = null
    this.activeCourseData = null
  }
}
</script>

<style lang="scss">
#avatar-col {
  width: 10rem;
}

#page-user-view {

  table {
    td {
      vertical-align: top;
      min-width: 140px;
      padding-bottom: .8rem;
      word-break: break-all;
    }

    &:not(.permissions-table) {
      td {
        @media screen and (max-width: 370px) {
          display: block;
        }
      }
    }
  }

  @media (min-width: 1200px) and (max-width: 1700px) {
    .image-course {
      display: none;
    }
    .course-col {
      width: 0 !important;
      min-height: 2.05vh;
    }
    .welcome-col {
      width: 100% !important;
    }
  }
}

</style>
